import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import { BingoUserDataContext } from '../../context/BingoUserDataContext';

const theme = {
  primary: '#B43361',
  secondary: '#F3D4D4',
  background: '#FFFFFF',
  text: '#000000',
};

const JoinGame = () => {
  const [pin, setPin] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useContext(BingoUserDataContext);

  // Load name and email from sessionStorage on mount
  useEffect(() => {
    const storedName = sessionStorage.getItem('playerName');
    const storedEmail = sessionStorage.getItem('playerEmail');
    if (storedName) setName(storedName);
    if (storedEmail) setEmail(storedEmail);
  }, []);

  const handleJoinGame = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`https://bosscrowns-api-a228488a1e46.herokuapp.com/bingo/${pin}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join game');
      }

      // Store name and email in sessionStorage
      sessionStorage.setItem('playerName', name);
      sessionStorage.setItem('playerEmail', email);

      setUser({ playerId: data.playerId, name, email, card: data.card });
      navigate(`/bingo/player/${pin}`, { state: { name, email, card: data.card } });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Optional: Clear stored name and email
  const handleClearStoredData = () => {
    sessionStorage.removeItem('playerName');
    sessionStorage.removeItem('playerEmail');
    setName('');
    setEmail('');
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        py: { xs: 3, sm: 5 },
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
          bgcolor: theme.background,
          textAlign: 'center',
        }}
      >
        <CasinoIcon
          sx={{
            fontSize: { xs: 40, sm: 60 },
            color: theme.primary,
            mb: { xs: 1, sm: 2 },
          }}
        />
        <Typography
          variant="h3"
          sx={{
            color: theme.text,
            fontWeight: 'bold',
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
          }}
        >
          Join Bingo Game
        </Typography>
        <form onSubmit={handleJoinGame}>
          <TextField
            fullWidth
            label="Game PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value.toUpperCase())}
            margin="normal"
            required
            disabled={isLoading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: theme.primary,
                },
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.9rem', sm: '1rem' },
                '&.Mui-focused': { color: theme.primary },
              },
            }}
          />
          <TextField
            fullWidth
            label="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
            disabled={isLoading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: theme.primary,
                },
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.9rem', sm: '1rem' },
                '&.Mui-focused': { color: theme.primary },
              },
            }}
          />
          <TextField
            fullWidth
            label="Your Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            type="email"
            required
            disabled={isLoading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: theme.primary,
                },
                fontSize: { xs: '0.9rem', sm: '1rem' },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.9rem', sm: '1rem' },
                '&.Mui-focused': { color: theme.primary },
              },
            }}
          />
          {error && (
            <Typography
              color="error"
              sx={{
                mb: 2,
                fontSize: { xs: '0.9rem', sm: '1rem' },
              }}
            >
              {error}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            fullWidth
            sx={{
              bgcolor: theme.primary,
              '&:hover': { bgcolor: theme.secondary },
              padding: { xs: '8px 16px', sm: '10px 20px' },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              borderRadius: 8,
              mb: 2,
            }}
          >
            {isLoading ? <CircularProgress size={24} sx={{ color: theme.background }} /> : 'Join Game'}
          </Button>
          {(sessionStorage.getItem('playerName') || sessionStorage.getItem('playerEmail')) && (
            <Button
              variant="outlined"
              onClick={handleClearStoredData}
              disabled={isLoading}
              fullWidth
              sx={{
                color: theme.text,
                borderColor: theme.text,
                padding: { xs: '8px 16px', sm: '10px 20px' },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                borderRadius: 8,
              }}
            >
              Clear Stored Name/Email
            </Button>
          )}
        </form>
      </Box>
    </Container>
  );
};

export default JoinGame;